import React, { useState, useEffect, useCallback } from "react";
import { MdClose, MdSave, MdDomain } from "react-icons/md";
import toast from "react-hot-toast";
import { ROLES, ROLE_LABELS } from "context/WorkersContext";
import { useSectorPersonnel } from "context/SectorPersonnelContext";
import { getSectors } from "api/sectors";
import { registerKpiUser } from "api/workers";

const SECONDARY = "#f27405";

const ROLE_OPTIONS = [
  { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
  { value: ROLES.SUPERVISOR, label: ROLE_LABELS[ROLES.SUPERVISOR] },
  { value: ROLES.OPERATOR, label: ROLE_LABELS[ROLES.OPERATOR] },
];

function getWorkerSectorIds(workerId, sectorPersonnel, sectorsList) {
  if (!Array.isArray(sectorsList) || sectorsList.length === 0) return [];
  const ids = [];
  sectorsList.forEach((s) => {
    const p = sectorPersonnel[s.id];
    if (!p) return;
    if ((p.supervisorIds || []).includes(workerId) || (p.operatorIds || []).includes(workerId)) {
      ids.push(s.id);
    }
  });
  return ids;
}

export default function AddWorker({ open, onClose, onSuccess, editWorker = null }) {
  const isEdit = Boolean(editWorker?.id);
  const { sectorPersonnel } = useSectorPersonnel();
  const [sectorsList, setSectorsList] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [sectorsError, setSectorsError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    role: ROLES.OPERATOR,
    email: "",
    employeeId: "",
    sectorId: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    setSectorsError(null);
    try {
      const result = await getSectors();
      setSectorsList(result.data || []);
    } catch (err) {
      setSectorsError(err.response?.data?.message || err.message || "Failed to load sectors");
      setSectorsList([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchSectors();
  }, [open, fetchSectors]);

  useEffect(() => {
    if (open) {
      if (editWorker) {
        setForm({
          name: editWorker.name || "",
          role: editWorker.role || ROLES.OPERATOR,
          email: editWorker.email || "",
          employeeId: editWorker.employeeId || "",
          sectorId: getWorkerSectorIds(editWorker.id, sectorPersonnel, sectorsList)[0] || "",
        });
      } else {
        setForm({
          name: "",
          role: ROLES.OPERATOR,
          email: "",
          employeeId: "",
          sectorId: "",
        });
      }
      setErrors({});
    }
  }, [open, editWorker, sectorPersonnel, sectorsList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      if (prev[name]) delete next[name];
      if (prev._submit) delete next._submit;
      return next;
    });
  };

  const handleSectorChange = (e) => {
    setForm((prev) => ({ ...prev, sectorId: e.target.value }));
    setErrors((prev) => {
      const next = { ...prev };
      if (prev.sectorId) delete next.sectorId;
      if (prev._submit) delete next._submit;
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.role) nextErrors.role = "Role is required";
    if (!form.email.trim()) nextErrors.email = "Email is required";
    if (!form.employeeId.trim()) nextErrors.employeeId = "Employee ID is required";
    if (
      (form.role === ROLES.SUPERVISOR || form.role === ROLES.OPERATOR) &&
      !form.sectorId
    ) {
      nextErrors.sectorId = "Sector is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    const sectorIds = form.sectorId ? [form.sectorId] : [];
    const sector = form.sectorId ? sectorsList.find((s) => s.id === form.sectorId) : null;
    const sectorName = sector?.name || "";

    if (isEdit) {
      // Edit: no API yet, use local update
      const payload = {
        id: editWorker.id,
        name: form.name.trim(),
        role: form.role,
        email: form.email.trim() || "",
        employeeId: form.employeeId.trim() || "",
        sectorIds,
      };
      if (typeof onSuccess === "function") onSuccess(payload);
      setIsSubmitting(false);
      onClose();
      return;
    }

    // Add: call API
    try {
      const result = await registerKpiUser({
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        employee_id: form.employeeId.trim(),
        sector_name: sectorName,
      });

      const apiUser = result.user || result;
      const payload = {
        id: apiUser.id,
        name: apiUser.name || form.name.trim(),
        role: apiUser.role || form.role,
        email: apiUser.email || form.email.trim(),
        employeeId: apiUser.employee_id || form.employeeId.trim(),
        sectorIds,
        sector_name: apiUser.sector_name || sectorName || null,
      };

      if (typeof onSuccess === "function") onSuccess(payload);
      toast.success(result.message || "Worker registered successfully. Password sent to email.");
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to register worker";
      setErrors((prev) => ({ ...prev, _submit: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div
          className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <h2 className="text-lg font-black text-slate-900 dark:text-white">
              {isEdit ? "Edit worker" : "Add worker"}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close"
            >
              <MdClose className="text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {errors._submit && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">{errors._submit}</p>
              </div>
            )}
            <div>
              <label
                htmlFor="worker-name"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                id="worker-name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-offset-0 focus:outline-none ${
                  errors.name
                    ? "border-red-500 focus:ring-red-500/30"
                    : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="worker-role"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="worker-role"
                name="role"
                value={form.role}
                onChange={handleChange}
                disabled={isEdit}
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0e76a8]/30 focus:ring-offset-0 focus:outline-none ${
                  errors.role
                    ? "border-red-500"
                    : "border-slate-200 dark:border-slate-700"
                }`}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.role}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="worker-email"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="worker-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                readOnly={isEdit}
                placeholder="e.g. john@usibras.com (password sent here for new workers)"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-offset-0 focus:outline-none ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500/30"
                    : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="worker-employeeId"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Employee ID <span className="text-red-500">*</span>
              </label>
              <input
                id="worker-employeeId"
                name="employeeId"
                type="text"
                value={form.employeeId}
                onChange={handleChange}
                readOnly={isEdit}
                placeholder="e.g. USI-001"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-offset-0 focus:outline-none ${
                  errors.employeeId
                    ? "border-red-500 focus:ring-red-500/30"
                    : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                }`}
              />
              {errors.employeeId && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.employeeId}</p>
              )}
            </div>

            {(form.role === ROLES.SUPERVISOR || form.role === ROLES.OPERATOR) && (
              <div>
                <label htmlFor="worker-sector" className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  <MdDomain className="inline-block text-base mr-1 align-middle" />
                  Sector <span className="text-red-500">*</span>
                </label>
                <select
                  id="worker-sector"
                  value={form.sectorId}
                  onChange={handleSectorChange}
                  disabled={sectorsLoading || isEdit}
                  className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-offset-0 focus:outline-none disabled:opacity-60 ${
                    errors.sectorId
                      ? "border-red-500 focus:ring-red-500/30"
                      : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                  }`}
                >
                  <option value="">
                    {sectorsLoading ? "Loading sectors…" : "Select sector…"}
                  </option>
                  {sectorsList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {errors.sectorId && (
                  <p className="mt-1 text-xs font-medium text-red-500">{errors.sectorId}</p>
                )}
                {sectorsError && (
                  <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">{sectorsError}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: SECONDARY }}
              >
                <MdSave className="text-lg" />
                {isSubmitting ? "Saving…" : isEdit ? "Update" : "Add worker"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
